import { usd, ago, clock } from "../../../lib/dash";
import { Stat, Row, Chip } from "../Tile";

const Section = ({ title, testid, children }) => (
  <section data-testid={testid}>
    <p className="mb-1 font-mono2 text-[10px] uppercase tracking-wider text-muted-foreground">{title}</p>
    {children}
  </section>
);

const None = ({ testid, children }) => <p data-testid={testid} className="py-1 text-[12px] text-muted-foreground">{children}</p>;

const allowanceText = (s) => (s.allowance_unlimited ? `Owner allowance · ${usd(s.base_cap_usd)} a month (legacy unlimited)`
  : s.allowance_usd != null ? `${usd(s.allowance_usd)} (set here)${s.window === "month" ? " a month" : ""}`
    : s.owner_allowance ? `Owner allowance · ${usd(s.base_cap_usd)} a month` : `Default · ${usd(s.base_cap_usd)}`);

const SpendRows = ({ s }) => (
  <>
    <Row dense label="Allowance" testid="account-allowance" value={allowanceText(s)} />
    <Row dense label={s.window === "month" ? "Remaining this month" : "Remaining"} testid="account-remaining" value={usd(s.remaining_usd)} tone={s.blocked ? "red" : s.reached ? "amber" : null} />
    <Row dense label="Recorded spend, all time" testid="account-spent" value={usd(s.spent_usd)} />
    {s.forgiven_usd > 0 && <Row dense label="Given back so far" testid="account-forgiven" value={usd(s.forgiven_usd)} />}
    {s.exempt && <Row dense label="Rate limits" value="Exempt (spending still capped)" />}
  </>
);

const Ducks = ({ ducks }) => (ducks.length ? (
  <ul data-testid="account-recent-ducks">
    {ducks.map((k) => (
      <li key={k.id} className="flex items-center justify-between gap-2 border-b border-border/60 py-1 text-[12.5px] last:border-0">
        <span className="min-w-0 truncate font-medium">{k.name || "Unnamed duck"}</span>
        <span className="shrink-0 text-[11px] text-muted-foreground">{k.build_status || k.status || ""} · {ago(k.created_at)}</span>
      </li>
    ))}
  </ul>
) : <None testid="account-no-ducks">No ducks yet.</None>);

const Credits = ({ credits }) => (credits.length ? (
  <ul data-testid="account-credit-list">
    {credits.map((c, i) => (
      <li key={`${c.order_name}-${i}`} className="flex items-center justify-between gap-2 border-b border-border/60 py-1 text-[12.5px] last:border-0">
        <span className="min-w-0 truncate">{c.order_name} · {c.duck_name || "duck"}</span>
        <span className="flex shrink-0 items-center gap-1.5 tabular-nums">{usd(c.amount_usd)} <Chip tone={c.state === "granted" ? "green" : "amber"}>{c.state}</Chip></span>
      </li>
    ))}
  </ul>
) : <None testid="account-no-credits">No order credits.</None>);

// Ducks, the spending allowance and the order credits of one account (read only).
export const SpendSummary = ({ d }) => {
  const s = d.spend;
  return (
    <div data-testid="account-summary" className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <Stat testid="account-ducks" label="Ducks" value={d.ducks.count} />
        <Stat testid="account-spend-used" label="Allowance used" value={usd(s.used_usd)} sub={`of ${usd(s.cap_usd)}${s.window === "month" ? " this month" : ""}`} tone={s.reached ? "amber" : null} />
        <Stat testid="account-credits" label="Order credits" value={usd(s.credits_usd)} sub={`${d.credits.length} order${d.credits.length === 1 ? "" : "s"}`} />
      </div>
      <Section title="Spending allowance" testid="account-spend"><SpendRows s={s} /></Section>
      <Section title="Newest ducks"><Ducks ducks={d.ducks.recent} /></Section>
      <Section title="Order credits"><Credits credits={d.credits} /></Section>
      {s.adjustments?.length > 0 && (
        <Section title="Allowance changes" testid="account-adjustments">
          {s.adjustments.slice().reverse().map((x) => (
            <Row key={x.at} dense label={clock(x.at)} value={x.note || "no note"} />
          ))}
        </Section>
      )}
    </div>
  );
};
